import { createContext, ReactNode, useEffect, useMemo, useState, useContext } from 'react'

import { useRouter } from 'next/router'
import { getSelectorsByUserAgent } from 'react-device-detect'
import { useEffectOnce } from 'react-use'

import {
  checkIsMapsAppView,
  checkIsNaverAppView,
  checkHasNaverClipEditor,
  checkHasNaverClipViewer,
  getBrowserReferrer,
  getBrowserUserAgent,
  getMapAppVersion,
  getMapsReferrer,
  isFromMapsApp,
  isFromNa,
  isFromNaverApp,
  isFromOpenMain,
  MapAppVersion,
  setMapsReferrer,
} from '@web/libs/entry.helper'
import { getHashFromRouter } from '@web/libs/utils'

interface GlobalStaticState {
  // 진입한 서비스 (entry?)
  isFromNa: boolean
  isFromMapsApp: boolean
  isFromOpenMain: boolean
  isFromNaverApp: boolean
  isFromMapsAppBottomSheet: boolean
  hasMapsAppSDK: boolean
  hasNaverAppSDK: boolean | null
  hasNaverClipEditor: boolean | null
  hasNaverClipViewer: boolean | null
  mapAppVersion: null | MapAppVersion
  // 디바이스 / 브라우저 환경
  isMobile: boolean
  isAndroid: boolean

  // 앱 내 의미를 두고 있는 special query params
  from: string | undefined

  // request header의 referer
  referrer: string | undefined
  isSsr: boolean
  mapsReferrer?: string | null
  isLogin: boolean
  userAgent: string
}

const GlobalStaticStateContext = createContext<GlobalStaticState>({
  isFromNa: false,
  isFromMapsApp: false,
  isFromOpenMain: false,
  isFromNaverApp: false,
  isFromMapsAppBottomSheet: false,
  hasMapsAppSDK: false,
  hasNaverAppSDK: null,
  hasNaverClipEditor: null,
  hasNaverClipViewer: null,
  mapAppVersion: null,
  isMobile: false,
  from: undefined,
  referrer: undefined,
  isSsr: true,
  mapsReferrer: null,
  isAndroid: false,
  isLogin: false,
  userAgent: '',
})

export interface ServerProps {
  referrer?: string
  userAgent?: string
  path?: string
  acceptLanguage?: string
  SSRDateTime: string
  userIdno?: string
}

interface Props {
  serverProps: ServerProps
  children?: ReactNode
}

export const GlobalStaticStateContextProvider = ({ serverProps, children }: Props) => {
  const router = useRouter()
  const hash = getHashFromRouter(router)
  const browserReferrer = getBrowserReferrer()
  const browserUserAgent = getBrowserUserAgent()

  const { referrer: serverReferrer, userAgent: serverUserAgent } = serverProps

  const mapsReferrerQuery = router.query.referer?.toString()

  const referrer = serverReferrer || browserReferrer
  const userAgent = serverUserAgent || browserUserAgent || ''

  const selector = userAgent ? getSelectorsByUserAgent(userAgent) : {}
  const { isMobile, isAndroid } = selector

  const mapAppVersion = useMemo(() => getMapAppVersion(userAgent), [userAgent])

  const [staticState, setStaticState] = useState<GlobalStaticState>(() => ({
    isFromNa: isFromNa(hash),
    isFromMapsApp: isFromMapsApp(userAgent),
    isFromOpenMain: isFromOpenMain(router.query.napp?.toString()),
    isFromNaverApp: isFromNaverApp(userAgent),
    isFromMapsAppBottomSheet: router.query.mapsAppBottomSheet === 'true',
    hasMapsAppSDK: false,
    hasNaverAppSDK: null,
    hasNaverClipEditor: null,
    hasNaverClipViewer: null,
    mapAppVersion,
    from: router.query.from?.toString(),
    isMobile,
    referrer,
    isSsr: true,
    mapsReferrer: router.query.referer?.toString(),
    isAndroid,
    isLogin: !!serverProps.userIdno,
    userAgent,
  }))

  useEffect(() => {
    setStaticState(prev => ({ ...prev, isFromNa: isFromNa(hash) }))
  }, [hash])

  useEffect(() => {
    setStaticState(prev => ({
      ...prev,
      hasMapsAppSDK: checkIsMapsAppView({ mapAppVersion }),
    }))
  }, [mapAppVersion])

  useEffect(() => {
    setStaticState(prev => ({
      ...prev,
      hasNaverAppSDK: checkIsNaverAppView(userAgent),
      hasNaverClipEditor: checkHasNaverClipEditor(userAgent),
      hasNaverClipViewer: checkHasNaverClipViewer(userAgent),
    }))
  }, [userAgent])

  useEffectOnce(() => {
    setStaticState(prev => ({
      ...prev,
      isSsr: false,
    }))
  })

  useEffect(() => {
    if (mapsReferrerQuery) {
      setMapsReferrer(mapsReferrerQuery)
    }

    setStaticState(prev => ({
      ...prev,
      mapsReferrer: getMapsReferrer(),
    }))
  }, [mapsReferrerQuery])

  return <GlobalStaticStateContext.Provider value={staticState}>{children}</GlobalStaticStateContext.Provider>
}

export const useGlobalStaticStateContext = () => useContext(GlobalStaticStateContext)
